import { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import mapboxgl, { Map as MapboxMap, Marker } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { type EventWithCircuit } from '../../api/eventsService';
import { type CircuitWithGeoJSON } from '../../api/circuitsService';
import { MAPBOX_ACCESS_TOKEN } from '../../utils/constants';
import { formatDateRange } from '../../utils/dateUtils';

// Set Mapbox access token
mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

interface MapViewProps {
  events: EventWithCircuit[];
  selectedEventId?: string;
  onMarkerClick: (event: EventWithCircuit) => void;
  onGoToEvent?: (event: EventWithCircuit) => void;
  onClearSelection?: () => void;
  circuitsCache: Record<string, CircuitWithGeoJSON>;
  theme: 'light' | 'dark';
}

export interface MapViewRef {
  zoomToCircuit: (event: EventWithCircuit) => Promise<void>;
}

export const MapView = forwardRef<MapViewRef, MapViewProps>(({ events, selectedEventId, onMarkerClick, onGoToEvent, onClearSelection, circuitsCache, theme }, ref) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<MapboxMap | null>(null);
  const markers = useRef<Map<string, Marker>>(new Map());
  const currentPopup = useRef<mapboxgl.Popup | null>(null);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    zoomToCircuit: async (event: EventWithCircuit) => {
      if (!map.current || !event.circuit.latitude || !event.circuit.longitude) return;

      // Fly to the circuit location
      map.current.flyTo({
        center: [event.circuit.longitude, event.circuit.latitude],
        zoom: 14,
        duration: 1500,
      });

      // Get circuit data from cache using circuit_geojson_id if available
      try {
        const geojsonId = event.circuit_geojson_id || event.circuit_id;
        const circuitData = circuitsCache[geojsonId];
        
        if (circuitData?.geojson && map.current) {
          // Remove existing circuit layers if any
          if (map.current.getLayer('circuit-line')) {
            map.current.removeLayer('circuit-line');
          }
          if (map.current.getLayer('circuit-stations')) {
            map.current.removeLayer('circuit-stations');
          }
          if (map.current.getLayer('circuit-stations-labels')) {
            map.current.removeLayer('circuit-stations-labels');
          }
          if (map.current.getSource('circuit')) {
            map.current.removeSource('circuit');
          }
          if (map.current.getSource('circuit-stations')) {
            map.current.removeSource('circuit-stations');
          }

          // Add circuit source and layer
          map.current.addSource('circuit', {
            type: 'geojson',
            data: circuitData.geojson as GeoJSON.GeoJSON,
          });

          map.current.addLayer({
            id: 'circuit-line',
            type: 'line',
            source: 'circuit',
            filter: ['==', ['geometry-type'], 'LineString'],
            paint: {
              'line-color': '#0E447D',
              'line-width': 3,
            },
          });

          // Add weather stations if available
          if (event.stations && event.stations.length > 0) {
            
            const stationsGeoJSON = {
              type: 'FeatureCollection' as const,
              features: event.stations.map((station) => ({
                type: 'Feature' as const,
                geometry: {
                  type: 'Point' as const,
                  coordinates: [station.lon, station.lat],
                },
                properties: {
                  reference: station.reference || 'N/A',
                  altitude: station.altitude,
                  nearest_segment_direction: station.nearest_segment_direction,
                },
              })),
            };

            map.current.addSource('circuit-stations', {
              type: 'geojson',
              data: stationsGeoJSON,
            });

            map.current.addLayer({
              id: 'circuit-stations',
              type: 'circle',
              source: 'circuit-stations',
              paint: {
                'circle-radius': 8,
                'circle-color': '#FFA500',
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff',
              },
            });

            // Add station reference labels
            map.current.addLayer({
              id: 'circuit-stations-labels',
              type: 'symbol',
              source: 'circuit-stations',
              layout: {
                'text-field': ['get', 'reference'],
                'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
                'text-size': 11,
                'text-offset': [0, 0],
                'text-anchor': 'center',
                'text-allow-overlap': true,
                'text-ignore-placement': true,
              },
              paint: {
                'text-color': '#ffffff',
                'text-halo-color': '#FFA500',
                'text-halo-width': 4,
                'text-halo-blur': 0,
              },
            });
            
            console.log('Station labels layer added'); // Debug: confirm layer was added

            // Add click handler for weather stations
            map.current.on('click', 'circuit-stations', (e) => {
              if (!e.features || e.features.length === 0) return;

              const feature = e.features[0];
              const geometry = feature.geometry as unknown as { type: string; coordinates: [number, number] };
              const coordinates = geometry.coordinates.slice() as [number, number];
              const properties = feature.properties as { reference: string; altitude: number; nearest_segment_direction: number };

              // Clear event selection so clicking event marker again will work
              if (onClearSelection) {
                onClearSelection();
              }

              // Delay popup creation to ensure state updates have completed
              // This prevents the event popup cleanup effect from removing the station popup
              setTimeout(() => {
                // Close any existing popup
                if (currentPopup.current) {
                  currentPopup.current.remove();
                  currentPopup.current = null;
                }

                // Create popup content
                const popupContent = document.createElement('div');
                popupContent.style.padding = '12px';
                popupContent.style.minWidth = '180px';
                popupContent.style.background = theme === 'dark' ? '#1a1a1a' : '#ffffff';
                popupContent.style.color = theme === 'dark' ? '#e0e0e0' : '#000000';

                const title = document.createElement('div');
                title.style.fontSize = '14px';
                title.style.fontWeight = 'bold';
                title.style.marginBottom = '8px';
                title.style.color = '#FFA500';
                title.textContent = `Station ${properties.reference}`;

                const altitudeDiv = document.createElement('div');
                altitudeDiv.style.fontSize = '12px';
                altitudeDiv.style.marginBottom = '4px';
                altitudeDiv.innerHTML = `<strong>Altitude:</strong> ${properties.altitude}m`;

                const directionDiv = document.createElement('div');
                directionDiv.style.fontSize = '12px';
                directionDiv.innerHTML = `<strong>Direction:</strong> ${properties.nearest_segment_direction.toFixed(2)}°`;

                popupContent.appendChild(title);
                popupContent.appendChild(altitudeDiv);
                popupContent.appendChild(directionDiv);

                // Create and show popup
                const popup = new mapboxgl.Popup({
                  closeButton: true,
                  closeOnClick: true,
                  offset: 15,
                  className: theme === 'dark' ? 'mapboxgl-popup-dark' : 'mapboxgl-popup-light'
                })
                  .setLngLat(coordinates)
                  .setDOMContent(popupContent)
                  .addTo(map.current!);

                currentPopup.current = popup;
              }, 0);
            });

            // Change cursor on hover
            map.current.on('mouseenter', 'circuit-stations', () => {
              if (map.current) {
                map.current.getCanvas().style.cursor = 'pointer';
              }
            });

            map.current.on('mouseleave', 'circuit-stations', () => {
              if (map.current) {
                map.current.getCanvas().style.cursor = '';
              }
            });
          }
        }
      } catch (error) {
        console.error('Failed to load circuit data:', error);
      }
    },
  }), [circuitsCache, theme, onClearSelection]);

  // Initialize map (only once)
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: theme === 'dark' 
        ? 'mapbox://styles/mapbox/dark-v11'
        : 'mapbox://styles/mapbox/light-v11',
      center: [0, 20],
      zoom: 1.2,
      pitch: 0,
      bearing: 0,
      projection: 'mercator', // Use flat Mercator projection instead of globe
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-left');

    // Resize map after initialization to fix gray areas
    map.current.once('load', () => {
      map.current?.resize();
      // Additional resize after tiles start loading
      setTimeout(() => map.current?.resize(), 50);
      setTimeout(() => map.current?.resize(), 200);
      setTimeout(() => map.current?.resize(), 500);
    });

    // Setup ResizeObserver to handle container size changes
    const resizeObserver = new ResizeObserver(() => {
      map.current?.resize();
    });

    if (mapContainer.current) {
      resizeObserver.observe(mapContainer.current);
    }

    return () => {
      resizeObserver.disconnect();
      map.current?.remove();
      map.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Theme is intentionally read only once for initial setup; style updates are handled separately

  // Update map style when theme changes (instead of recreating the map)
  useEffect(() => {
    if (!map.current) return;

    const newStyle = theme === 'dark' 
      ? 'mapbox://styles/mapbox/dark-v11'
      : 'mapbox://styles/mapbox/light-v11';

    // Wait for style to be loaded before updating to avoid "Style is not done loading" warning
    const updateStyle = () => {
      if (!map.current) return;
      map.current.setStyle(newStyle);
      // Ensure projection is set to mercator after style change
      map.current.setProjection('mercator');
    };

    if (map.current.isStyleLoaded()) {
      updateStyle();
    } else {
      map.current.once('style.load', updateStyle);
    }

    // Resize after style change to fix gray areas
    const resizeHandler = () => {
      setTimeout(() => {
        map.current?.resize();
      }, 100);
    };

    map.current.once('styledata', resizeHandler);

    return () => {
      // Cleanup: try to remove the event listener if it hasn't fired yet
      map.current?.off('styledata', resizeHandler);
      map.current?.off('style.load', updateStyle);
    };
  }, [theme]);

  // Update markers when events change
  useEffect(() => {
    if (!map.current) return;

    // Clear existing markers
    markers.current.forEach((marker) => marker.remove());
    markers.current.clear();

    // Add new markers
    events.forEach((event) => {
      if (!event.circuit.latitude || !event.circuit.longitude) return;

      const el = document.createElement('div');
      el.className = 'map-marker';
      el.style.width = '16px';
      el.style.height = '16px';
      el.style.borderRadius = '50%';
      el.style.backgroundColor = '#ffffff';
      el.style.border = '3px solid #0E447D';
      el.style.cursor = 'pointer';
      el.style.boxShadow = '0 2px 8px rgba(14, 68, 125, 0.4)';
      el.style.transition = 'box-shadow 0.3s ease, border-color 0.3s ease';

      // Add hover effect (without transform to avoid positioning issues)
      el.addEventListener('mouseenter', () => {
        el.style.boxShadow = '0 4px 16px rgba(14, 68, 125, 0.8)';
        el.style.borderColor = '#1558A0';
      });

      el.addEventListener('mouseleave', () => {
        el.style.boxShadow = '0 2px 8px rgba(14, 68, 125, 0.4)';
        el.style.borderColor = '#0E447D';
      });

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([event.circuit.longitude, event.circuit.latitude])
        .addTo(map.current!);

      // Add click handler
      el.addEventListener('click', () => {
        onMarkerClick(event);
      });

      markers.current.set(event.circuit_id, marker);
    });
  }, [events, onMarkerClick]);

  // Update selected marker styling
  useEffect(() => {
    markers.current.forEach((marker, id) => {
      const el = marker.getElement();
      if (id === selectedEventId) {
        el.style.width = '20px';
        el.style.height = '20px';
        el.style.backgroundColor = '#0E447D';
        el.style.border = '3px solid #ffffff';
        el.style.zIndex = '1000';
      } else {
        el.style.width = '16px';
        el.style.height = '16px';
        el.style.backgroundColor = '#ffffff';
        el.style.border = '3px solid #0E447D';
        el.style.zIndex = '1';
      }
    });
  }, [selectedEventId]);

  // Show popup for selected event (without flying)
  useEffect(() => {
    // Remove any existing popup
    if (currentPopup.current) {
      currentPopup.current.remove();
      currentPopup.current = null;
    }

    if (!map.current || !selectedEventId) return;

    const selectedEvent = events.find((e) => e.circuit_id === selectedEventId);
    if (!selectedEvent?.circuit.latitude || !selectedEvent?.circuit.longitude) return;

    // Create popup DOM element
    const popupContent = document.createElement('div');
    popupContent.style.padding = '12px';
    popupContent.style.minWidth = '200px';
    popupContent.style.background = theme === 'dark' ? '#1a1a1a' : '#ffffff';
    popupContent.style.color = theme === 'dark' ? '#e0e0e0' : '#000000';

    // Header with flag and name
    const header = document.createElement('div');
    header.style.display = 'flex';
    header.style.alignItems = 'center';
    header.style.gap = '8px';
    header.style.marginBottom = '8px';
    
    const flag = document.createElement('img');
    flag.src = `https://flagcdn.com/24x18/${selectedEvent.circuit.country.toLowerCase()}.png`;
    flag.alt = selectedEvent.circuit.country;
    flag.style.width = '24px';
    flag.style.height = '18px';
    flag.style.objectFit = 'cover';
    
    const title = document.createElement('strong');
    title.style.fontSize = '14px';
    title.textContent = selectedEvent.circuit.display_name;
    
    header.appendChild(flag);
    header.appendChild(title);

    // Event name
    const eventName = document.createElement('div');
    eventName.style.fontSize = '12px';
    eventName.style.color = theme === 'dark' ? '#b0b0b0' : '#666';
    eventName.style.marginBottom = '8px';
    eventName.textContent = selectedEvent.name;

    // Date range
    const dateRange = document.createElement('div');
    dateRange.style.fontSize = '11px';
    dateRange.style.color = theme === 'dark' ? '#909090' : '#888';
    dateRange.textContent = formatDateRange(selectedEvent.start_date, selectedEvent.end_date);

    // Go button
    const goButton = document.createElement('button');
    goButton.style.marginTop = '12px';
    goButton.style.width = '100%';
    goButton.style.padding = '6px 12px';
    goButton.style.background = '#0E447D';
    goButton.style.color = 'white';
    goButton.style.border = 'none';
    goButton.style.borderRadius = '4px';
    goButton.style.cursor = 'pointer';
    goButton.style.fontSize = '12px';
    goButton.style.fontWeight = '500';
    goButton.textContent = 'Go →';
    
    // Button hover effects
    goButton.addEventListener('mouseenter', () => {
      goButton.style.background = '#1558A0';
    });
    goButton.addEventListener('mouseleave', () => {
      goButton.style.background = '#0E447D';
    });
    
    // Button click handler
    goButton.addEventListener('click', () => {
      if (onGoToEvent) {
        onGoToEvent(selectedEvent);
      }
    });

    // Assemble the popup content
    popupContent.appendChild(header);
    popupContent.appendChild(eventName);
    popupContent.appendChild(dateRange);
    popupContent.appendChild(goButton);

    // Create and show popup
    const popup = new mapboxgl.Popup({ 
      closeButton: true, 
      closeOnClick: false,  // Don't close when clicking map
      offset: 25,
      className: theme === 'dark' ? 'mapboxgl-popup-dark' : 'mapboxgl-popup-light'
    })
      .setLngLat([selectedEvent.circuit.longitude, selectedEvent.circuit.latitude])
      .setDOMContent(popupContent)
      .addTo(map.current);

    currentPopup.current = popup;

    // Cleanup on unmount or when effect re-runs
    return () => {
      if (currentPopup.current) {
        currentPopup.current.remove();
        currentPopup.current = null;
      }
    };
  }, [selectedEventId, events, theme, onGoToEvent]);

  return (
    <div
      ref={mapContainer}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    />
  );
});

MapView.displayName = 'MapView';

